// Configuração central da API Xano

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

const rawBaseUrl = (import.meta as any).env?.VITE_XANO_URL as
  | string
  | undefined;
const rawEnv =
  ((import.meta as any).env?.VITE_ENV as string | undefined) || 'dev';

// Normaliza env vindo das envs: aceita 'dev' | 'prd' com ou sem dois-pontos
function normalizeEnv(env: string | undefined): 'dev' | 'prd' {
  const cleaned = String(env || '')
    .toLowerCase()
    .replace(/^:/, '');
  return cleaned === 'prd' ? 'prd' : 'dev';
}

export const ENV: 'dev' | 'prd' = normalizeEnv(rawEnv);
export const BASE_URL: string = (rawBaseUrl || '').replace(/\/$/, '');

/** Destino do e-mail de efetivação ao comercial em ambiente DEV (SOS-10163). */
export const EMAIL_COMERCIAL_DEV = 'teste@paoetalho.com.br';

if (!BASE_URL) {
  // Ajuda de debug no ambiente de desenvolvimento
}

// Monta a URL completa para um endpoint do Xano
// versionKey: identificador do grupo (ex.: '9-ms6pAp', 'rEsr7KLt', 'Lm-VXhVx', 'BiIQTjXy', 'kgStOPqy', '_XyGQ1NG')
// IMPORTANTE: Xano usa workspaces separados (dev/live) - forçando dev no versionKey
export function buildApiPath(versionKey: string, endpointPath: string): string {
  // Forçar ambiente dev adicionando :dev ao versionKey se ENV for dev
  const envSuffix = ENV === 'dev' ? ':dev' : '';
  const version = `api:${versionKey}${envSuffix}`;
  const path = String(endpointPath || '').replace(/^\//, '');
  const fullUrl = `${BASE_URL}/${version}/${path}`;

  return fullUrl;
}

type ApiFetchInit = RequestInit & {
  versionKey: string;
  query?: Record<
    string,
    string | number | boolean | (string | number | boolean)[] | undefined
  >;
  /** Não envia Authorization — use em auth/login e fluxos anônimos. */
  skipAuth?: boolean;
  /** Não limpa authToken em 401 — use logo após login enquanto valida auth/me. */
  preserveSessionOn401?: boolean;
};

function toQueryString(params: ApiFetchInit['query']): string {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach(v => usp.append(key, String(v)));
    } else {
      usp.set(key, String(value));
    }
  });
  const s = usp.toString();
  return s ? `?${s}` : '';
}

function defaultHeaders(skipAuth = false): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
  };
  // Apenas em desenvolvimento enviamos a origem de dados explícita
  if (ENV === 'dev') {
    headers['X-Data-Source'] = 'dev';
  }
  if (!skipAuth) {
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('authToken')
        : null;
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return headers;
}

export async function apiFetch<T = any>(
  endpointPath: string,
  init: ApiFetchInit
): Promise<T> {
  const { versionKey, query, headers, method, body, skipAuth, preserveSessionOn401, ...rest } = init;
  const url = buildApiPath(versionKey, endpointPath) + toQueryString(query);

  const finalHeaders = { ...defaultHeaders(skipAuth), ...(headers || {}) };

  const res = await fetch(url, {
    method: (method as HttpMethod) || 'GET',
    headers: finalHeaders,
    body,
    ...rest,
  });

  // Trata 404 de forma consistente
  if (res.status === 404) {
    const text = await res.text().catch(() => '');
    // Para alguns endpoints, 404 é comportamento esperado (ex: buscar contato que não existe)
    // Vamos deixar o tratamento específico para cada serviço
    const error = new Error(text || 'Recurso não encontrado (404)');
    (error as any).status = 404;
    (error as any).statusText = res.statusText;
    throw error;
  }
  if (!res.ok) {
    let msg = '';
    let apiPayload: unknown;
    let apiCode: unknown;
    try {
      const data = await res.json();
      apiPayload = data?.payload;
      apiCode = data?.code;
      msg =
        (data && (data.message || data.error || JSON.stringify(data))) || '';
    } catch {
      msg = await res.text().catch(() => '');
    }

    // Tratamento específico para token expirado (401)
    // Só remove o token se houver um token no localStorage (evita remover durante registro)
    if (res.status === 401) {
      const hasToken =
        typeof localStorage !== 'undefined' &&
        localStorage.getItem('authToken');
      const preserveSession = preserveSessionOn401 === true;

      if (!preserveSession) {
        // Limpa dados sensíveis independentemente de ainda haver token
        localStorage.removeItem('user_id');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('wallet');

        // Só remove o token (e avisa listeners) se realmente houver um token válido salvo
        if (hasToken) {
          localStorage.removeItem('authToken');

          // Disparar evento para notificar outros componentes
          window.dispatchEvent(new CustomEvent('auth-expired'));

          // Se a mensagem contém "expired", usar mensagem mais amigável
          if (msg.toLowerCase().includes('expired')) {
            msg = 'Sua sessão expirou. Faça login novamente.';
          }
        }
      }
      // Se não houver token, o 401 é esperado (ex: endpoints que requerem auth durante registro)
      // A mensagem de erro será lançada normalmente, mas será tratada pelo serviço específico
      // Incluímos o status no erro para facilitar o tratamento
      const error = new Error(msg || `Erro ${res.status} ${res.statusText}`);
      (error as any).status = res.status;
      (error as any).statusText = res.statusText;
      (error as any).payload = apiPayload;
      (error as any).code = apiCode;
      throw error;
    }

    const error = new Error(msg || `Erro ${res.status} ${res.statusText}`);
    (error as any).status = res.status;
    (error as any).statusText = res.statusText;
    (error as any).payload = apiPayload;
    (error as any).code = apiCode;
    throw error;
  }
  // Tenta json; se falhar, retorna como texto
  try {
    const result = (await res.json()) as T;
    return result;
  } catch {
    const result = (await res.text()) as unknown as T;
    return result;
  }
}

export function getJson<T = any>(
  versionKey: string,
  path: string,
  query?: ApiFetchInit['query'],
  init?: Omit<ApiFetchInit, 'versionKey' | 'query' | 'method'>
): Promise<T> {
  return apiFetch<T>(path, {
    versionKey,
    method: 'GET',
    query,
    ...(init || {}),
  });
}

export function postJson<T = any>(
  versionKey: string,
  path: string,
  body?: any,
  init?: Omit<ApiFetchInit, 'versionKey' | 'body' | 'method'>
): Promise<T> {
  return apiFetch<T>(path, {
    versionKey,
    method: 'POST',
    body: body != null ? JSON.stringify(body) : undefined,
    ...(init || {}),
  });
}

export function putJson<T = any>(
  versionKey: string,
  path: string,
  body?: any,
  init?: Omit<ApiFetchInit, 'versionKey' | 'body' | 'method'>
): Promise<T> {
  return apiFetch<T>(path, {
    versionKey,
    method: 'PUT',
    body: body != null ? JSON.stringify(body) : undefined,
    ...(init || {}),
  });
}

export function patchJson<T = any>(
  versionKey: string,
  path: string,
  body?: any,
  init?: Omit<ApiFetchInit, 'versionKey' | 'body' | 'method'>
): Promise<T> {
  return apiFetch<T>(path, {
    versionKey,
    method: 'PATCH',
    body: body != null ? JSON.stringify(body) : undefined,
    ...(init || {}),
  });
}

export function del(
  versionKey: string,
  path: string,
  init?: Omit<ApiFetchInit, 'versionKey' | 'method'>
): Promise<any> {
  return apiFetch(path, { versionKey, method: 'DELETE', ...(init || {}) });
}

// Export adicional útil em debug
export const __debug = { BASE_URL, ENV, buildApiPath };
